// SEQ-180 follow-up (14 September 2026): what the page shows while the first
// request is in flight.
//
// It used to be two grey rectangles, 120px and 320px tall, which told the
// reader nothing about what was coming and shifted the whole page when the
// real cards replaced them.
//
// THEN IT WAS A GUESS, WHICH WAS ALSO WRONG. The second version hardcoded four
// quarter tiles and two half-width charts. Design review measured it against
// the real thing: the skeleton grid ended around y=630 and the settled grid
// around y=1390, a 760px jump that took the wages note down the page with it.
// The tiles now come from `skeletonTiles`, which reads THIS dashboard's own
// saved spans, so the placeholder is the shape the page is about to be.
//
// NO JAVASCRIPT ANIMATION. The shimmer is the app's own `.skeleton` class
// (src/index.css), a CSS gradient sweep that runs on the compositor. A
// loading state is precisely when the main thread is busiest, so the one
// perpetual animation on this page is the one that never touches it.
import "./PnlDashboardGrid.css";
import "./PnlDashboardSkeleton.css";
import { skeletonTiles, type SkeletonSource } from "../../features/pnlAnalytics/skeletonTiles";

/** Bar heights for the chart skeletons. Uneven on purpose: a flat row of equal bars reads as a table, not a chart. */
const BAR_HEIGHTS = [42, 68, 55, 80, 34, 62, 74, 48];

function KpiTileSkeleton() {
  return (
    <div className="pnl-skeleton-card">
      <div className="pnl-skeleton-row">
        <div className="skeleton pnl-skeleton-label" />
        <div className="skeleton pnl-skeleton-icon" />
      </div>
      <div className="skeleton pnl-skeleton-value" />
      <div className="skeleton pnl-skeleton-delta" />
    </div>
  );
}

function ChartCardSkeleton() {
  return (
    <div className="pnl-skeleton-card">
      <div className="skeleton pnl-skeleton-title" />
      <div className="skeleton pnl-skeleton-caption" />
      <div className="pnl-skeleton-bars">
        {BAR_HEIGHTS.map((height, index) => (
          <div key={index} className="skeleton pnl-skeleton-bar" style={{ height: `${height}%` }} />
        ))}
      </div>
      <div className="skeleton pnl-skeleton-axis" />
    </div>
  );
}

export interface PnlDashboardSkeletonProps {
  /**
   * The widgets this dashboard is about to render, with their saved spans.
   * Known before the first request, because the request plan is built from the
   * same list.
   */
  widgets: readonly SkeletonSource[];
}

/**
 * `role="status"` carries the only thing a screen reader needs here. The
 * shapes themselves are decoration for the eye and are hidden from the
 * accessibility tree, because "heading, heading, heading" read out of an empty
 * skeleton is noise.
 */
export function PnlDashboardSkeleton({ widgets }: PnlDashboardSkeletonProps) {
  const tiles = skeletonTiles(widgets);
  return (
    <div>
      <p role="status" className="pnl-visually-hidden">
        Loading this dashboard.
      </p>
      <div className="pnl-dashboard-grid" aria-hidden="true">
        {tiles.map((tile) => (
          <div key={tile.key} className="pnl-widget" data-w={tile.layout.w} data-h={tile.layout.h}>
            {tile.kind === "kpi" ? <KpiTileSkeleton /> : <ChartCardSkeleton />}
          </div>
        ))}
      </div>
    </div>
  );
}
